import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LuBriefcaseBusiness } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';

const SLUG = 'cfo-advisory';

const ICON_OPTIONS = ['LuBriefcase', 'LuTrendingUp', 'LuFileText', 'LuUsers', 'LuLightbulb', 'LuRocket', 'LuHandshake'];

const emptyFeatureCard = (order) => ({
  icon: 'LuTrendingUp',
  title: '',
  description: '',
  order,
  isActive: true,
});

const defaultForm = () => ({
  isPublished: true,
  pageTitle: 'CFO Advisory',
  pageSubtitle: '',
  pageIcon: 'LuBriefcase',
  cardIcon: 'LuBriefcase',
  cardTitle: '',
  cardBody: '',
  footerNote: '',
  featureCards: [emptyFeatureCard(0)],
});

const inputClass = 'w-full text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-2';
const labelClass = 'block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1';

const Field = ({ label, value, onChange, multiline = false }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {multiline ? (
      <textarea className={inputClass} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const CfoAdvisoryPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('page');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          featureCards: (c.featureCards?.length ? c.featureCards : defaultForm().featureCards).map((card, i) => ({
            ...emptyFeatureCard(i),
            ...card,
            order: card.order ?? i,
          })),
        });
      }
    } catch {
      toastError('Failed to load CFO Advisory page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateFeatureCard = (index, field, value) =>
    setForm((prev) => {
      const featureCards = [...prev.featureCards];
      featureCards[index] = { ...featureCards[index], [field]: value };
      return { ...prev, featureCards };
    });

  const addFeatureCard = () =>
    setForm((prev) => ({
      ...prev,
      featureCards: [...prev.featureCards, emptyFeatureCard(prev.featureCards.length)],
    }));

  const removeFeatureCard = (index) =>
    setForm((prev) => ({
      ...prev,
      featureCards: prev.featureCards.filter((_, i) => i !== index),
    }));

  const moveFeatureCard = (index, dir) =>
    setForm((prev) => {
      const featureCards = [...prev.featureCards];
      const next = index + dir;
      if (next < 0 || next >= featureCards.length) return prev;
      [featureCards[index], featureCards[next]] = [featureCards[next], featureCards[index]];
      return {
        ...prev,
        featureCards: featureCards.map((card, i) => ({ ...card, order: i })),
      };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...form,
        featureCards: form.featureCards.map((card, index) => ({ ...card, order: index })),
      };
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: payload, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('CFO Advisory page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded CFO Advisory defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      await fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="CFO Advisory Service Page"
        subtitle="Edit user portal CFO Advisory page header, main card, and feature highlights"
        icon={LuBriefcaseBusiness}
        onBack={() => navigate('/portal-cms')}
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {['page', 'features'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {tab === 'page' ? 'Page & Main Card' : 'Feature Cards'}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 space-y-4 mb-6">
        {activeTab === 'page' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Page title" value={form.pageTitle} onChange={(v) => updateField('pageTitle', v)} />
            <Field label="Page icon" value={form.pageIcon} onChange={(v) => updateField('pageIcon', v)} />
            <div className="md:col-span-2">
              <Field label="Page subtitle" value={form.pageSubtitle} onChange={(v) => updateField('pageSubtitle', v)} multiline />
            </div>
            <Field label="Card icon" value={form.cardIcon} onChange={(v) => updateField('cardIcon', v)} />
            <Field label="Card title" value={form.cardTitle} onChange={(v) => updateField('cardTitle', v)} />
            <div className="md:col-span-2">
              <Field label="Card body" value={form.cardBody} onChange={(v) => updateField('cardBody', v)} multiline />
            </div>
            <div className="md:col-span-2">
              <Field label="Footer note" value={form.footerNote} onChange={(v) => updateField('footerNote', v)} multiline />
            </div>
            <p className="md:col-span-2 text-xs text-gray-500">Icon names: {ICON_OPTIONS.join(', ')}</p>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="space-y-4">
            {form.featureCards.map((card, index) => (
              <div key={index} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Feature {index + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveFeatureCard(index, -1)} className="p-1.5 rounded hover:bg-gray-100"><FaArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveFeatureCard(index, 1)} className="p-1.5 rounded hover:bg-gray-100"><FaArrowDown className="w-3 h-3" /></button>
                    <button type="button" onClick={() => removeFeatureCard(index)} className="p-1.5 rounded text-red-500"><FaTrash className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Icon</label>
                    <select
                      className={inputClass}
                      value={card.icon || 'LuTrendingUp'}
                      onChange={(e) => updateFeatureCard(index, 'icon', e.target.value)}
                    >
                      {ICON_OPTIONS.map((icon) => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  <Field label="Title" value={card.title} onChange={(v) => updateFeatureCard(index, 'title', v)} />
                  <div className="md:col-span-2">
                    <Field label="Description" value={card.description} onChange={(v) => updateFeatureCard(index, 'description', v)} multiline />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={card.isActive !== false} onChange={(e) => updateFeatureCard(index, 'isActive', e.target.checked)} />
                  Active
                </label>
              </div>
            ))}
            <button type="button" onClick={addFeatureCard} className="flex items-center gap-2 text-sm text-primary-600 font-medium">
              <FaPlus className="w-3 h-3" /> Add feature card
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
          <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm font-medium flex items-center gap-2">
          <FaUndo /> {resetting ? 'Resetting...' : 'Reset to Defaults'}
        </button>
        <label className="flex items-center gap-2 text-sm ml-auto">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>
      </div>
    </PageLayout>
  );
};

export default CfoAdvisoryPortalCMS;
